/**
 * Motion helpers.
 *
 * The rule for this app: entrances animate once, nothing loops, and
 * `prefers-reduced-motion` is honoured without exception. Both hooks here read
 * that preference themselves so a caller cannot forget.
 */
import { useEffect, useRef, useState } from 'react'

/**
 * Subscribes to a media query. Used for the reduced-motion preference and for
 * the one layout decision that cannot be made in CSS — whether the AI panel is
 * a column or a sheet, which changes *which component renders*, not just how it
 * looks.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )

  useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = () => setMatches(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** True when the visitor has asked the system to reduce motion. Live-updating. */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)')
}

/**
 * Counts a figure up to its value on first paint, then tracks it exactly.
 *
 * Only the first appearance animates — a number that re-animates every time a
 * query refetches is noise, and in a finance tool a moving figure reads as a
 * changing figure.
 */
export function useCountUp(value: number | null, durationMs = 550): number | null {
  const prefersReduced = usePrefersReducedMotion()
  const [display, setDisplay] = useState<number | null>(
    prefersReduced ? value : value === null ? null : 0,
  )
  const hasAnimated = useRef(prefersReduced)

  useEffect(() => {
    if (value === null) {
      setDisplay(null)
      return
    }

    if (hasAnimated.current || prefersReduced) {
      setDisplay(value)
      return
    }

    hasAnimated.current = true
    const start = performance.now()
    let frame = 0

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      // ease-out cubic: fast arrival, quiet settle.
      setDisplay(value * (1 - Math.pow(1 - t, 3)))
      if (t < 1) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [value, durationMs, prefersReduced])

  return display
}
